import { FormEvent, useMemo, useState } from 'react';
import { SubContentProps } from '../../LanguageSetting';
import useWorkingHoursChangeForm, {
  WorkingHoursChangeForm,
} from './useWorkingHoursChangeForm';
import { useSnackbar } from '@/hooks/global-tools';
import { useTranslation } from 'react-i18next';
import { SelectOption } from '@/components/@IntranetLibrary/Select';
import { FormProviderProps } from '@/components/HookForm/FormProvider';
import { fetchApi } from '@/utils/api/customAxios';
import { AxiosError } from 'axios';
import { useSWRConfig } from 'swr';
import dayjs from 'dayjs';

export const WORKING_HOURS_OPTIONS: SelectOption[] = [
  { label: '08:00~17:00', value: '08:00~17:00' },
  { label: '08:30~17:30', value: '08:30~17:30' },
  { label: '09:00~18:00', value: '09:00~18:00' },
  { label: '09:30~18:30', value: '09:30~18:30' },
  { label: '10:00~19:00', value: '10:00~19:00' },
];

// "09:00~18:00" → '0900' / '1800'
export const splitWorkingHours = (
  workingHours: string,
): { workingStartHour: string; workingEndHour: string } => {
  const [start = '', end = ''] = workingHours.split('~');
  return {
    workingStartHour: start.replace(':', ''),
    workingEndHour: end.replace(':', ''),
  };
};

// '0900' / '1800' → "09:00~18:00"
export const joinWorkingHours = (
  workingStartHour: string,
  workingEndHour: string,
): string => {
  const fmt = (hhmm: string) => `${hhmm.slice(0, 2)}:${hhmm.slice(2, 4)}`;
  return `${fmt(workingStartHour)}~${fmt(workingEndHour)}`;
};

export type WorkingHoursChangeProps = {
  readonly formProps: FormProviderProps<WorkingHoursChangeForm>;
  readonly currentWorkingHours: string;
  readonly lastChangedDate: string;
  readonly newWorkingHoursOptions: SelectOption[];
  readonly startYearMonthOptions: SelectOption[];
  readonly selectedNewWorkingHours: string;
  readonly selectedStartYearMonth: string;
  readonly isSubmitLoading: boolean;
  readonly isSubmitDisabled: boolean;
  readonly onClose: () => void;
};

const useWorkingHoursChangeProps = (
  settings: SubContentProps,
): WorkingHoursChangeProps => {
  const { t } = useTranslation();
  const { menuOnClose } = settings;
  const { showSuccess, showError } = useSnackbar();
  const { mutate } = useSWRConfig();

  const methods = useWorkingHoursChangeForm();
  const {
    handleSubmit,
    watch,
    formState: { isValid },
  } = methods;

  const selectedNewWorkingHours = watch('newWorkingHours');
  const selectedStartYearMonth = watch('startYearMonth');

  const currentWorkingHoursValue = settings.myInfo.workingHours;
  const isFnCo = settings.myInfo.companyCode === 'N';

  const currentWorkingHours =
    WORKING_HOURS_OPTIONS.find((opt) => opt.value === currentWorkingHoursValue)
      ?.label ?? currentWorkingHoursValue;

  // TODO: 실제 API 연동 시 교체
  const lastChangedDate = '2026-02-01';

  const HALF_HOUR_OPTIONS = new Set(['08:30~17:30', '09:30~18:30']);

  // 현재 근무시간도 선택 가능 (반차 근무시간은 법인 N만 노출)
  const filteredOptions = useMemo(() => {
    return WORKING_HOURS_OPTIONS.filter(
      (opt) => isFnCo || !HALF_HOUR_OPTIONS.has(opt.value),
    );
  }, [isFnCo]);

  // 근무 시간 변경 시작 월 옵션: 다음 달 ~ +3개월 (값 포맷 'YYYY-MM' 유지)
  const startYearMonthOptions: SelectOption[] = useMemo(() => {
    const base = dayjs().add(1, 'month').startOf('month');
    const m1 = base.format('YYYY-MM');
    const m2 = base.add(1, 'month').format('YYYY-MM');
    const m3 = base.add(2, 'month').format('YYYY-MM');
    return [
      { label: m1, value: m1 },
      { label: m2, value: m2 },
      { label: m3, value: m3 },
    ];
  }, []);

  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  const handleFormOnSubmit = (e: FormEvent) => {
    e.preventDefault();

    handleSubmit(async (data: WorkingHoursChangeForm) => {
      try {
        setIsSubmitLoading(true);

        const { workingStartHour, workingEndHour } = splitWorkingHours(
          data.newWorkingHours,
        );

        await fetchApi({
          method: 'PUT',
          url: `/api/v1/passport/me/working-hours`,
          data: {
            workingStartHour,
            workingEndHour,
            changedAt: `${data.startYearMonth}`,
          },
        });

        mutate(`/api/v1/passport/me/working-hours/histories`);
        showSuccess({ message: t('근무 시간 변경 신청이 완료되었습니다.') });
        menuOnClose?.();
      } catch (e) {
        const message =
          e instanceof AxiosError
            ? e.response?.data?.message ??
              t('근무 시간 변경 신청에 실패했습니다.')
            : t('근무 시간 변경 신청에 실패했습니다.');
        showError({ message });
      } finally {
        setIsSubmitLoading(false);
      }
    })();
  };

  return {
    formProps: {
      methods,
      onSubmit: handleFormOnSubmit,
      id: 'mypage-working-hours-change',
    },
    currentWorkingHours,
    lastChangedDate,
    newWorkingHoursOptions: filteredOptions,
    startYearMonthOptions,
    selectedNewWorkingHours,
    selectedStartYearMonth,
    isSubmitLoading,
    isSubmitDisabled: !isValid,
    onClose: menuOnClose ?? (() => undefined),
  };
};

export default useWorkingHoursChangeProps;
